import React, { useState } from 'react';
import ContactInfo from './ContactInfo';
import Assignment from './Assignment';
import Notes from './Notes';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { X } from 'lucide-react';
import { leadsApi } from '../services/leadsApi';
import { toast } from '@/hooks/use-toast';

interface ChatContactInfoProps {
    contact: {
        id?: string;
        name: string;
        company: string;
        email: string;
        phone: string;
        value: string;
        stage: string;
        ia_activa?: boolean;
    };
    isOpen: boolean;
    onClose: () => void;
}

const ChatContactInfo: React.FC<ChatContactInfoProps> = ({ contact, isOpen, onClose }) => {
    const [iaActiva, setIaActiva] = useState(contact.ia_activa ?? true);
    const [updatingIA, setUpdatingIA] = useState(false);

    const handleToggleIA = async (checked: boolean) => {
        if (!contact.id) {
            toast.error('No se puede actualizar: ID del lead no disponible');
            return;
        }

        setUpdatingIA(true);
        try {
            await leadsApi.updateLead(contact.id, { ia_activa: checked });
            setIaActiva(checked);
            toast.success(checked ? 'IA activada para este contacto' : 'IA desactivada para este contacto');
        } catch (error) {
            console.error('Error actualizando estado IA:', error);
            toast.error('No se pudo actualizar el estado de la IA');
        } finally {
            setUpdatingIA(false);
        }
    };

    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 z-50 h-full flex items-end justify-end">
            <div className="bg-black h-full bg-opacity-30 absolute inset-0" onClick={onClose} />
            <div className="relative h-full flex items-center">
                <div className="animate-slide-in-right h-full flex flex-col bg-white w-[400px] shadow-xl">
                    {/* Header with name and IA toggle */}
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-lg text-maxi-text-dark">{contact.name}</span>
                            {/* IA Toggle inline */}
                            <div className="flex items-center gap-2">
                                <Switch
                                    checked={iaActiva}
                                    onCheckedChange={handleToggleIA}
                                    disabled={updatingIA || !contact.id}
                                />
                                <span className={`text-xs font-medium ${iaActiva ? 'text-green-600' : 'text-gray-500'}`}>
                                    {iaActiva ? 'IA' : 'IA Off'}
                                </span>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={onClose}
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                    {/* Content */}
                    <div className="flex-1 overflow-y-auto flex flex-col gap-6 p-6">
                        <ContactInfo contact={contact} onEdit={() => { }} />
                        <Assignment />
                        <Notes />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatContactInfo;
